import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import CloudSavedIcon from '@atlaskit/icon-lab/core/cloud-saved';
import PageIcon from '@atlaskit/icon/core/page';
import { Inline } from '@atlaskit/primitives/compiled';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';
import { fetchSuggestions } from '../utils/fetch';

const getSuggestions = async ({
	api,
	contentAri,
	forceAdf,
	generate,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
	forceAdf?: boolean;
	generate: boolean;
}) => {
	if (!contentAri || forceAdf) {
		const doc = await api?.core?.actions.requestDocumentAsync();
		const suggestions = await fetchSuggestions({
			contentAdf: JSON.stringify(doc),
			contentAri: '',
			generate,
		});
		api?.core?.actions?.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SET_SUGGESTIONS',
				suggestions,
			});
			return tr;
		});
		return;
	}
	const suggestions = await fetchSuggestions({ contentAri, generate });
	api?.core?.actions?.execute(({ tr }) => {
		tr.setMeta(aiSuggestionsPluginKey, {
			action: 'SET_SUGGESTIONS',
			suggestions,
		});
		return tr;
	});
};

export const SuggestionsToolbarButton = ({
	api,
	contentAri,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const handleForceGenerate = useCallback(() => {
		getSuggestions({ api, contentAri, generate: true, forceAdf: true });
	}, [api, contentAri]);

	const handleUseERS = useCallback(() => {
		getSuggestions({ api, contentAri, generate: false, forceAdf: false });
	}, [api, contentAri]);

	const handleUseAdf = useCallback(() => {
		getSuggestions({ api, contentAri, generate: true, forceAdf: true });
	}, [api, contentAri]);

	return (
		<Inline space="space.050">
			<Button appearance="subtle" onClick={handleForceGenerate}>
				{formatMessage(aiSuggestionsMessages.suggestionsToolbarButtonLabel)}
			</Button>
			<IconButton icon={CloudSavedIcon} label="" onClick={handleUseERS} />
			<IconButton icon={PageIcon} label="" onClick={handleUseAdf} />
		</Inline>
	);
};

/**
 * Old suggestions toolbar button that opened the staging area modal
 */
// const OldSuggestionsToolbarButton = ({
// 	api,
// 	providerFactory,
// }: {
// 	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
// 	providerFactory: ProviderFactory;
// }): React.JSX.Element => {
// 	const { formatMessage } = useIntl();
// 	const [isModalOpen, setIsModalOpen] = useState(false);
// 	const [adf, setAdf] = useState<DocNode | null>(null);

// 	const handleButtonClick = useCallback(() => {
// 		if (!isModalOpen) {
// 			api?.core?.actions.requestDocument((doc) => {
// 				setAdf(doc as DocNode);
// 				setIsModalOpen(true);
// 			});
// 		}
// 	}, [api, isModalOpen]);

// 	const handleCloseModal = useCallback(() => {
// 		setIsModalOpen(false);
// 	}, []);

// 	return (
// 		<>
// 			<Button appearance="subtle" onClick={handleButtonClick}>
// 				{formatMessage(aiSuggestionsMessages.suggestionsToolbarButtonLabel)}
// 			</Button>
// 			{isModalOpen && adf && (
// 				<StagingAreaModal
// 					api={api}
// 					adf={adf}
// 					onClose={handleCloseModal}
// 					providerFactory={providerFactory}
// 				/>
// 			)}
// 		</>
// 	);
// };
